import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'system' | 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

/**
 * Theme Store
 *
 * Manages the application theme preference with localStorage persistence.
 * The theme is applied to the DOM by adding/removing the 'dark' class on the <html> element.
 *
 * Theme modes:
 * - system: Follow OS dark mode preference
 * - light: Force light mode
 * - dark: Force dark mode
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      setTheme: (theme: Theme) => {
        set({ theme });
        applyTheme(theme);
      },
    }),
    {
      name: 'theme-storage', // localStorage key
      onRehydrateStorage: () => (state) => {
        // Apply theme from localStorage on app load
        if (state) {
          applyTheme(state.theme);
        }
      },
    }
  )
);

/**
 * Apply theme to DOM
 * Updates the <html> element's class based on theme preference
 */
function applyTheme(theme: Theme): void {
  const root = window.document.documentElement;

  // Remove existing theme class
  root.classList.remove('dark');

  if (theme === 'dark') {
    // Force dark mode
    root.classList.add('dark');
  } else if (theme === 'system') {
    // Follow system preference
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (systemPrefersDark) {
      root.classList.add('dark');
    }
  }
  // For 'light' theme, we don't add any class (default is light mode)
}

/**
 * Initialize theme on app load
 * Call this in your main App component
 */
export function initializeTheme(): void {
  const { theme } = useThemeStore.getState();
  applyTheme(theme);

  // Listen for system theme changes when in 'system' mode
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const currentTheme = useThemeStore.getState().theme;
    if (currentTheme === 'system') {
      applyTheme('system');
    }
  });
}
